import { Suspense } from 'react'
import { Link } from 'react-router-dom'
import { styled, useMediaQuery, useTheme } from '@mui/material'
import Typography from '@mui/material/Typography'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import IconButton from '@mui/material/IconButton'
import Button from '@mui/material/Button'
import Skeleton from '@mui/material/Skeleton'
import ArrowCircleRightOutlinedIcon from '@mui/icons-material/ArrowCircleRightOutlined'

const StyledPaper = styled(Paper)(({ theme, background, variant }) => ({
  position: 'relative',
  height: '100%',
  minHeight: variant
    ? theme.card.variant[variant]?.minHeight ||
      theme.card.variant.default.minHeight
    : theme.card.variant.default.minHeight,
  padding: variant
    ? theme.card.variant[variant]?.padding || theme.card.variant.default.padding
    : theme.card.variant.default.padding,
  overflow: 'hidden',
  borderRadius:
    theme.card.variant[variant]?.borderRadius || theme.card.borderRadius,
  background: (() => {
    if (variant === 'track') return theme.vars.palette.track[background]

    return background
      ? theme.vars.gradient[background]
      : theme.vars.card.background
  })(),
  '& .link-trigger': {
    position: 'static',
  },
  '& .link-trigger::before': {
    position: 'absolute',
    inset: 0,
    content: '""',
  },
}))

function CardBase(props) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const {
    title = '',
    titleTag = 'h2',
    titleVariant,
    link = { label: '', href: '' },
    contrast,
    variant,
    children,
    background,
  } = {
    ...props,
    contrast: !!props.background,
    titleVariant: (() => {
      if (props.variant === 'small') return 'h2-small'
      if (props.variant === 'track') return 'h1'

      return 'h2'
    })(),
  }

  return (
    <Suspense
      fallback={
        <Skeleton
          variant="rounded"
          animation="wave"
          width={'100%'}
          height={200}
        />
      }
    >
      <StyledPaper
        variant={variant}
        background={background || theme.card.background}
        elevation={0}
      >
        <Stack
          sx={{ height: '100%' }}
          direction="column"
          spacing={1.6}
          useFlexGap
        >
          {title && (
            <Stack
              direction="row"
              spacing={0.8}
              sx={{
                justifyContent:
                  variant === 'track' ? 'flex-start' : 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography
                variant={titleVariant}
                component={titleTag}
                color={contrast ? 'text.contrast' : 'text.primary'}
              >
                {title}
              </Typography>
              {isMobile && link.href && (
                <IconButton
                  component={Link}
                  className="link-trigger"
                  size="large"
                  to={link.href}
                >
                  <ArrowCircleRightOutlinedIcon
                    sx={{ color: contrast ? 'text.contrast' : 'primary.main' }}
                  />
                </IconButton>
              )}
              {!isMobile && link.href && link.label && (
                <Button
                  LinkComponent={Link}
                  variant="text"
                  size="small"
                  sx={{ color: 'primary.light' }}
                  endIcon={<ArrowCircleRightOutlinedIcon />}
                  to={link.href}
                >
                  {link.label}
                </Button>
              )}
              {!isMobile && link.href && !link.label && (
                <IconButton
                  component={Link}
                  size={variant === 'track' ? 'medium' : 'small'}
                  className="link-trigger"
                  to={link.href}
                >
                  <ArrowCircleRightOutlinedIcon
                    fontSize={variant === 'track' ? 'medium' : 'small'}
                    sx={{ color: contrast ? 'text.contrast' : '' }}
                  />
                </IconButton>
              )}
            </Stack>
          )}
          {children}
        </Stack>
      </StyledPaper>
    </Suspense>
  )
}

export default CardBase
